import { AxiosError } from 'axios'
import { GraphQLError } from 'graphql'
import { Required } from 'utility-types'
import { FieldValidationError } from './FieldValidationError'
import { GitHubTokenValidationError } from './GitHubTokenValidationError'
import { GraphQLResponseError } from './GraphQLResponseError'
import { JiraPermissionsValidationError } from './JiraPermissionsValidationError'
import { NotFoundError } from './NotFoundError'

/**
 * `isAxiosError` is a type guard similar to `axios.isAxiosError`
 * but it also allows to define response data type using type parameter
 */
export const isAxiosError = <T = unknown>(
  error: unknown
): error is AxiosError<T> => (error as AxiosError)?.isAxiosError

/**
 * `isAxiosError` is a type guard similar to `axios.isAxiosError`
 * but it also allows to define response data type using type parameter
 * and verifies that `error.response` is not empty.
 */
export const isAxiosErrorWithResponse = <T = unknown>(
  error: unknown
): error is Required<AxiosError<T>, 'response'> =>
  isAxiosError<T>(error) && !!error.response?.status && !!error.response?.data

export const isEmptyGraphError = (error: unknown) => {
  if (isAxiosError<{ error?: { message: string } }>(error)) {
    return error.response?.data?.error?.message === 'empty graph'
  }
  return false
}

export const isNoTeamsAvailableError = (error: unknown) => {
  if (isAxiosError<{ errors: Array<GraphQLError> }>(error)) {
    const [err] = error?.response?.data?.errors ?? []
    return err?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED'
  }
  return false
}

/**
 * `isFieldValidationError` is a type guard to determine
 * if argument is instance of `FieldValidationError`
 */
export const isFieldValidationError = (
  error: unknown
): error is FieldValidationError =>
  (error as FieldValidationError)?.isFieldValidationError

/**
 * `isFieldValidationError` is a type guard to determine
 * if argument is instance of `GitHubTokenValidationError`
 */
export const isGitHubScopeValidationError = (
  error: unknown
): error is GitHubTokenValidationError =>
  (error as GitHubTokenValidationError)?.isGitHubTokenValidationError

/**
 * `isFieldValidationError` is a type guard to determine
 * if argument is instance of `JiraPermissionsValidationError`
 */
export const isJiraPermissionsValidationError = (
  error: unknown
): error is JiraPermissionsValidationError =>
  (error as JiraPermissionsValidationError)?.isJiraPermissionsValidationError

/**
 * `isGraphQLResponseError` is a type guard to determine
 * if argument is instance of `GraphQLResponseError`
 */
export const isGraphQLResponseError = (
  error: unknown
): error is GraphQLResponseError =>
  (error as GraphQLResponseError)?.isGraphQLResponseError

/**
 * `isNotFoundError` is a type guard to determine
 * if argument is instance of `FieldValidationError`
 */
export const isNotFoundError = (error: unknown): error is NotFoundError =>
  (error as NotFoundError)?.isNotFoundError
